import React from 'react';
import { useIntl } from 'react-intl';
import { AssetRow } from '../listHelpers';

interface AssetCartDrawerProps {
  open: boolean;
  siteLabel: string;
  rows: AssetRow[];
  submitting: boolean;
  onToggle: () => void;
  onRemove: (row: AssetRow) => void;
  onClear: () => void;
  onSubmit: () => void;
}

/**
 * Summary of the templates staged for this site. Nothing is deployed until
 * "Add assets to site" is pressed, so the cart is safe to build up and abandon.
 */
const AssetCartDrawer: React.FC<AssetCartDrawerProps> = ({
  open,
  siteLabel,
  rows,
  submitting,
  onToggle,
  onRemove,
  onClear,
  onSubmit,
}) => {
  const intl = useIntl();

  return (
    <div className={`asset-cart ${open ? 'asset-cart--open' : ''}`}>
      <button type="button" className="asset-cart__handle" onClick={onToggle}>
        <i className={`uil uil-angle-${open ? 'right' : 'left'}`} />
        <span>
          {intl.formatMessage({ id: 'assets.cart.title' })} ({rows.length})
        </span>
      </button>

      {open && (
        <div className="asset-cart__panel">
          <div className="asset-cart__header">
            <h4>{intl.formatMessage({ id: 'assets.cart.title' })}</h4>
            {siteLabel && (
              <p className="asset-cart__subtitle">
                {intl.formatMessage({ id: 'assets.cart.selectedFor' }, { site: siteLabel })}
              </p>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="asset-cart__empty">{intl.formatMessage({ id: 'assets.cart.empty' })}</p>
          ) : (
            <ul className="asset-cart__list">
              {rows.map((row) => (
                <li key={row.key} className="asset-cart__row">
                  <div>
                    <span className="asset-cart__name">{row.name}</span>
                    <span className="asset-cart__area">{row.area}</span>
                  </div>
                  <button
                    type="button"
                    className="asset-cart__remove"
                    onClick={() => onRemove(row)}
                    aria-label={intl.formatMessage({ id: 'assets.cart.remove' })}
                    title={intl.formatMessage({ id: 'assets.cart.remove' })}
                  >
                    <i className="uil uil-trash-alt" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {rows.length > 0 && (
            <button type="button" className="asset-cart__clear" onClick={onClear}>
              {intl.formatMessage({ id: 'assets.cart.clear' })}
            </button>
          )}

          <button
            type="button"
            className="btn btn-danger asset-cart__submit"
            disabled={rows.length === 0 || submitting}
            onClick={onSubmit}
          >
            {submitting
              ? intl.formatMessage({ id: 'assets.cart.adding' })
              : intl.formatMessage({ id: 'assets.cart.submit' })}
          </button>
        </div>
      )}
    </div>
  );
};

export default AssetCartDrawer;
